import React, { useState } from 'react';
// 1. Import motion and AnimatePresence from framer-motion
import { motion, AnimatePresence } from 'framer-motion';
import { FaCommentDots, FaWhatsapp } from 'react-icons/fa';
import { CiFacebook } from "react-icons/ci";

// 2. Define reusable animation variants for clean and consistent animations
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1,
    },
  },
};

const slideIn = (direction = 'left') => ({
  initial: {
    x: direction === 'left' ? -100 : direction === 'right' ? 100 : 0,
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'tween',
      duration: 0.8,
      ease: 'easeOut',
    },
  },
});

export default function Home() {
  const features = [
    {
      title: "Website Chat",
      icon: <FaCommentDots />,
      description: "AI-powered interface with real-time translation for 35+ languages.",
      image: "/images/Artboard1.png"
    },
    {
      title: "WhatsApp Integration",
      icon: <FaWhatsapp />,
      description: "Automated triage with seamless handoff to live agents.",
      image: "/images/Artboard2.png"
    },
    {
      title: "Facebook",
      icon: <CiFacebook />,
      description: "Priority queue with AI-powered sorting for maximum efficiency.",
      image: "/images/Artboard3.png"
    }
  ];

  // 3. State for the new Framer Motion mobile slider
  const [activeSlide, setActiveSlide] = useState(0);
  const [direction, setDirection] = useState(0);

  // Framer Motion variant for the slider animation
  const sliderVariants = {
    enter: (direction) => ({
      x: direction > 0 ? '100%' : '-100%',
      opacity: 0,
    }),
    center: {
      zIndex: 1,
      x: 0,
      opacity: 1,
    },
    exit: (direction) => ({
      zIndex: 0,
      x: direction < 0 ? '100%' : '-100%',
      opacity: 0,
    }),
  };

  const paginate = (newDirection) => {
    const newIndex = activeSlide + newDirection;
    if (newIndex < 0 || newIndex >= features.length) return;
    setDirection(newDirection);
    setActiveSlide(newIndex);
  };
  
  const handleDragEnd = (event, info) => {
    const swipeThreshold = 50;
    if (info.offset.x > swipeThreshold) {
      paginate(-1); // Swiped right
    } else if (info.offset.x < -swipeThreshold) {
      paginate(1); // Swiped left
    }
  };
  
  // The original touch handlers are no longer needed, Framer Motion handles gestures.

  return (
    <main className="font-sans text-gray-900 bg-white scroll-smooth overflow-x-hidden">
      {/* Hero Section */}
      <section className="relative flex min-h-screen items-center justify-center px-4 sm:px-6 lg:px-8 bg-white overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover opacity-20">
            <source src="/images/video1.mp4" type="video/mp4" />
            <div className="absolute inset-0 "></div>
          </video>
        </div>

        {/* Subtle Background SVG overlay */}
        <div className="absolute inset-0 z-1 opacity-10 bg-gradient-to-b from-white via-transparent to-white">
          <svg className="w-full h-full" viewBox="0 0 1440 600" preserveAspectRatio="xMidYMid slice">
            <path
              fill="#16a085"
              d="M0,600L80,573.3C160,547,320,493,480,480C640,467,800,493,960,506.7C1120,520,1280,520,1360,520L1440,520L1440,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z"
            />
          </svg>
        </div>

        {/* Hero Content */}
        <motion.div 
          className="relative z-10 max-w-7xl mx-auto text-center"
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <motion.h1 variants={fadeInUp} className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-gray-900 mb-6">
            <span className="block mb-4">Revolutionizing Support with</span>
            <span className="bg-gradient-to-r from-[#16a085] to-emerald-500 bg-clip-text text-transparent">
              AI-Driven Conversations
            </span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="mt-6 max-w-3xl mx-auto text-lg sm:text-md text-gray-600 font-medium">
            Unify <span className="font-semibold ">Website Chat</span>,{' '}
            <span className="font-semibold">WhatsApp</span>, and{' '}
            <span className="font-semibold ">Messenger</span> into one intelligent, secure, and scalable platform.
          </motion.p>
          <motion.div variants={fadeInUp} className="mt-10 flex flex-col sm:flex-row justify-center gap-4">
            <motion.a
              href="/request-demo"
              className="bg-[#16a085] text-white font-semibold px-8 py-3 rounded-lg shadow-lg"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Request a Personalized Demo
            </motion.a>
            <motion.a
              href="#how-it-works"
              className="border-2 border-[#16a085] text-[#16a085] font-semibold px-8 py-3 rounded-lg"
              whileHover={{ y: -5, scale: 1.05, backgroundColor: '#16a085', color: '#fff' }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Discover How It Works →
            </motion.a>
          </motion.div>
          <motion.div variants={fadeInUp} className="mt-16 flex flex-wrap justify-center items-center gap-6 text-gray-500">
            <span className='text-[#16a085] font-bold'> Trusted by:</span>
            <div className="flex items-center gap-6">
              <img src="/images/Dark-tech.png" alt="Dark" width="80px" className="hover:opacity-100 transition" />
              <img src="/images/e-qera-hiri.png" alt="Eqera" width="80px" className="hover:opacity-100 transition" />
              <img src="/images/e-visiton-hiri.png" alt="evizitoj" width="100px" className="hover:opacity-100 transition" />
            </div>
          </motion.div>
        </motion.div>
      </section>

      <motion.section 
        className="relative isolate overflow-hidden py-24 sm:py-32"
        initial="initial"
        whileInView="animate"
        viewport={{ once: true, amount: 0.3 }}
        variants={staggerContainer}
      >
        <div className="absolute inset-0 -z-10 h-full w-full bg-white bg-[radial-gradient(#16a085_1px,transparent_1px)] [background-size:16px_16px]"></div>
        <div className="absolute inset-x-0 top-[-10rem] -z-10 transform-gpu overflow-hidden blur-3xl sm:top-[-20rem]" aria-hidden="true">
          <div
            className="relative left-1/2 -z-10 aspect-[1155/678] w-[36.125rem] max-w-none -translate-x-1/2 rotate-[30deg] bg-gradient-to-tr from-[#1abc9c] to-[#16a085] opacity-30 sm:left-[calc(50%-40rem)] sm:w-[72.1875rem]"
            style={{ clipPath: 'polygon(74.1% 44.1%, 100% 61.6%, 97.5% 26.9%, 85.5% 0.1%, 80.7% 2%, 72.5% 32.5%, 60.2% 62.4%, 52.4% 68.1%, 47.5% 58.3%, 45.2% 34.5%, 27.5% 76.7%, 0.1% 64.9%, 17.9% 100%, 27.6% 76.8%, 76.1% 97.7%, 74.1% 44.1%)' }}
          />
        </div>

        <motion.div
          variants={fadeInUp}
          className="max-w-4xl mx-auto sm:px-6 lg:px-8 text-center bg-white p-6 rounded-xl shadow-xl hover:shadow-2xl transition-shadow duration-300"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 tracking-tighter">
            Transforming Customer Support for Today’s Digital World
          </h2>
          <p className="mt-6 text-lg text-gray-800 leading-relaxed">
            In today’s fast-paced digital landscape, customer expectations demand instant, personalized, and consistent support — across every channel they use. Void answers this call by consolidating all customer conversations into a centralized, AI-augmented system that empowers agents, administrators, and leadership to deliver superior service.
          </p>
          <p className="mt-4 text-lg text-gray-800 leading-relaxed">
            Whether your customers reach you via your website chat, WhatsApp, or Facebook Messenger, Void intelligently manages the flow of communication, ensuring no inquiry is lost, every conversation is transparent, and all interactions are efficient.
          </p>
          <div className="mt-5">
            <motion.a
              href="/About"
              className="inline-block bg-[#16a085] text-white font-semibold px-8 py-3 rounded-full shadow-lg"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Explore Features
            </motion.a>
          </div>
        </motion.div>
      </motion.section>

      {/* Stats Section with Stagger Animation */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <motion.div 
          className="max-w-7xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-8 text-center"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.5 }}
        >
          {[
            { value: '99.9%', label: 'Uptime' },
            { value: '3.2s', label: 'Avg Response' },
            { value: '68%', label: 'Deflection Rate' },
            { value: '150+', label: 'Integrations' }
          ].map((stat, index) => (
            <motion.div key={index} variants={fadeInUp}>
              <div className="text-4xl sm:text-5xl font-extrabold text-[#16a085] mb-2">{stat.value}</div>
              <div className="text-lg text-gray-600">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>
        {/* The old <style> tag is no longer needed as Framer Motion handles this */}
      </section>

      {/* Multi-Channel Section */}
     <section className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <motion.div 
          className="text-center mb-12"
          initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.8 }} variants={fadeInUp}
        >
          <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-4">Omnichannel Made Effortless</h2>
          <p className="max-w-2xl mx-auto text-lg text-gray-600">
            Seamlessly connect all customer touchpoints into one unified workflow
          </p>
        </motion.div>
        
        {/* DESKTOP: Animated Grid */}
        <motion.div
          className="hidden md:grid md:grid-cols-3 md:gap-8"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true, amount: 0.2 }}
        >
          {features.map((feature, index) => (
            <motion.div key={index} variants={fadeInUp} className="h-full">
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-2xl hover:-translate-y-2 transition-all duration-300 flex flex-col h-full">
                <div className="flex items-center mb-4">
                  <div className="text-3xl text-[#16a085] mr-4">{feature.icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900">{feature.title}</h3>
                </div>
                <p className="text-gray-600 mb-4 flex-grow">{feature.description}</p>
                <img src={feature.image} alt={feature.title} className="rounded-lg border border-gray-200 w-full mt-auto" />
              </div>
            </motion.div>
          ))}
        </motion.div>
        
        {/* MOBILE: Framer Motion Slider */}
        <div className="md:hidden relative h-[520px] overflow-hidden">
          <AnimatePresence initial={false} custom={direction}>
            <motion.div
              key={activeSlide}
              className="absolute w-full h-full p-2"
              custom={direction}
              variants={sliderVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ x: { type: "spring", stiffness: 300, damping: 30 }, opacity: { duration: 0.2 }}}
              drag="x"
              dragConstraints={{ left: 0, right: 0 }}
              dragElastic={0.5}
              onDragEnd={handleDragEnd}
            >
              <div className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 flex flex-col h-full">
                <div className="flex items-center mb-4">
                  <div className="text-3xl text-[#16a085] mr-4">{features[activeSlide].icon}</div>
                  <h3 className="text-xl font-semibold text-gray-900">{features[activeSlide].title}</h3>
                </div>
                <p className="text-gray-600 mb-4 flex-grow">{features[activeSlide].description}</p>
                <img src={features[activeSlide].image} alt={features[activeSlide].title} className="rounded-lg border border-gray-200 w-full mt-auto" />
              </div>
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Slider Navigation Dots - only visible on mobile */}
        <div className="flex justify-center mt-8 space-x-3 md:hidden">
          {features.map((_, index) => (
            <button
              key={index}
              onClick={() => {
                setDirection(index > activeSlide ? 1 : -1);
                setActiveSlide(index);
              }}
              className={`w-3 h-3 rounded-full transition-colors duration-300 ${activeSlide === index ? 'bg-gray-900' : 'bg-gray-300 hover:bg-gray-400'}`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </section>

      {/* Dashboard Showcase */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto" style={{ maxWidth: '1400px' }}>
          <motion.h2 variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.8 }} className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Unleash Enterprise-Grade AI Simplicity
          </motion.h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Text Content */}
            <motion.div 
              className="space-y-6"
              variants={staggerContainer}
              initial="initial"
              whileInView="animate"
              viewport={{ once: true, amount: 0.2 }}
            >
              <motion.p variants={fadeInUp} className="text-lg text-gray-600 leading-relaxed">
                Void’s SOC 2-certified platform combines military-grade security with intuitive interfaces, requiring minimal training for maximum impact.
              </motion.p>
              <ul className="space-y-4">
                {[
                  "Secure authentication & persistent sessions",
                  "Multi-chat interface with smart routing",
                  "Real-time messaging with Socket.IO",
                  "Superadmin control panel"
                ].map((item, index) => (
                  <motion.li key={index} variants={fadeInUp} className="flex items-start group">
                    <svg
                      className="w-6 h-6 text-[#16a085] mr-3 mt-1 group-hover:scale-110 transition-transform duration-300 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-gray-700 group-hover:text-gray-900 transition-colors duration-300">{item}</span>
                  </motion.li>
                ))}
              </ul>
              <motion.div variants={fadeInUp} className="mt-8">
                <motion.a
                  href="/request-demo"
                  className="inline-block bg-[#16a085] text-white font-semibold px-8 py-3 rounded-lg shadow-lg"
                  whileHover={{ y: -5, scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  Explore the Dashboard
                </motion.a>
              </motion.div>
            </motion.div>
            {/* Visual Content with SVG and Image */}
            <motion.div 
              variants={slideIn('right')} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.3 }}
              className="relative"
            >
              <div className="absolute inset-0 -z-10">
                <svg className="w-full h-full opacity-10" viewBox="0 0 600 400">
                  <circle cx="300" cy="200" r="150" fill="#16a085" fillOpacity="0.2" />
                  <path d="M100 300 Q300 100 500 300" stroke="#16a085" strokeWidth="4" fill="none" />
                </svg>
              </div>
              <img src="images/Dashboard.png" alt="Void Dashboard" className="rounded-xl shadow-2xl w-full transform hover:scale-[1.02] transition-transform duration-300" />
              <div className="absolute top-4 right-4 bg-white p-3 rounded-lg shadow-lg border border-gray-100 flex items-center gap-2">
                <div className="text-2xl font-bold text-[#16a085]">99.9%</div>
                <div className="text-xs font-medium text-gray-600">Uptime Guarantee</div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Agent & Admin Dashboard */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto" style={{ maxWidth: '1400px' }}>
          <motion.h2 variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.8 }} className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Empower Your Team with Real-Time Efficiency
          </motion.h2>
          
          {(() => {
            const features = [
              { title: "Secure Authentication & Persistent Sessions", description: "Agents log in with role-based credentials, with their active conversations preserved across sessions and page reloads, eliminating workflow disruption.", icon: ( <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.1-.9 2-2 2s-2-.9-2-2 2-4 2-4 2 2.9 2 4zm0 0c0 1.1.9 2 2 2s2-.9 2-2-2-4-2-4-2 2.9-2 4zm-6 7v-2c0-2.2 3.6-4 6-4s6 1.8 6 4v2H6z" /></svg> ) },
              { title: "Multi-Chat Interface with Smart Routing", description: "Featuring a three-panel layout, agents efficiently navigate between new requests, active chats, and conversation details — with real-time notifications and unread message indicators.", icon: ( <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7h18M3 12h18M3 17h18" /></svg> ) },
              { title: "Socket.IO Real-Time Messaging", description: "The dashboard ensures instant message delivery and dynamic event handling for both agents and customers, creating a fluid communication experience.", icon: ( <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg> ) },
              { title: "Superadmin Control Panel", description: "A dedicated, secure interface empowers superadmins to manage users, oversee chat history, and audit conversations — maintaining compliance and quality control.", icon: ( <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" /></svg> ) }
            ];
            return (
              <>
                {/* Mobile swiper */}
                <div className="md:hidden flex overflow-x-auto space-x-4 snap-x snap-mandatory pb-4 -mx-2 px-2">
                  {features.map((item, index) => (
                    <motion.div
                      key={index}
                      variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.5 }}
                      className="snap-start flex-shrink-0 w-80 bg-white p-6 rounded-xl shadow-lg border border-gray-100"
                    >
                      <div className="flex items-center mb-4">{item.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </motion.div>
                  ))}
                </div>
                {/* Desktop grid */}
                <motion.div 
                  variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}
                  className="hidden md:grid md:grid-cols-2 gap-8"
                >
                  {features.map((item, index) => (
                    <motion.div key={index} variants={fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                      <div className="flex items-center mb-4">{item.icon}</div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                      <p className="text-gray-600">{item.description}</p>
                    </motion.div>
                  ))}
                </motion.div>
              </>
            );
          })()}
          <motion.p variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true }} className="mt-12 text-center text-gray-600 italic text-lg max-w-3xl mx-auto">
            Engineered to optimize team collaboration, response times, and customer satisfaction.
          </motion.p>
        </div>
      </section>

      {/* Backend Architecture */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto" style={{ maxWidth: '1400px' }}>
          <motion.h2 variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.8 }} className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Future-Proof Backend Architecture
          </motion.h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              variants={slideIn('left')} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.3 }}
              className="relative"
            >
              <img src='images/data.png' alt="Backend Architecture Diagram" />
            </motion.div>
            <motion.div 
              variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}
              className="space-y-6"
            >
              <ul className="space-y-6 list-none text-gray-700 text-lg">
                {[
                  { title: "Node.js & Express Framework", description: "Modular API design with clear separation of concerns, supporting secure and scalable operations." },
                  { title: "MYSQL-Based Data Storage (Phase 1)", description: "Lightweight and portable data management for users, chat requests, and logs — with plans to transition to MongoDB or PostgreSQL to accommodate enterprise-scale demands." },
                  { title: "JWT-Secured Endpoints & Socket Connections", description: "Enforcing strict authentication and authorization, safeguarding sensitive communications and data." },
                  { title: "Channel-Agnostic Routing Logic", description: "Intelligent message routing adapts dynamically to customer origin (web, WhatsApp, Messenger), enabling seamless user experience." }
                ].map((item, index) => (
                  <motion.li key={index} variants={fadeInUp} className="flex items-start gap-4 group">
                    <div className="flex-shrink-0 w-10 h-10 bg-[#16a085] text-white rounded-full flex items-center justify-center text-lg font-bold group-hover:bg-[#13876b] transition-colors duration-300">
                      {index + 1}
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 group-hover:text-[#16a085] transition-colors duration-300">{item.title}</h3>
                      <p className="mt-2 text-gray-600">{item.description}</p>
                    </div>
                  </motion.li>
                ))}
              </ul>
              <motion.p variants={fadeInUp} className="mt-6 text-gray-600 italic text-lg">
                This foundation ensures Void remains performant and reliable, adaptable to evolving customer support requirements.
              </motion.p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Why Choose Void */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto" style={{ maxWidth: '1400px' }}>
          <motion.h2 variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.8 }} className="text-4xl sm:text-5xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Why Void is Your Ultimate Solution
          </motion.h2>
          <div className="relative">
            <div className="absolute inset-0 -z-10 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 1400 400">
                <circle cx="700" cy="200" r="200" fill="#16a085" fillOpacity="0.3" />
                <path d="M200 300 Q700 100 1200 300" stroke="#16a085" strokeWidth="6" fill="none" />
              </svg>
            </div>
            {(() => {
              const features = [
                { title: "Unified Customer Experience", description: "Erase communication silos with a single platform that consolidates all channels into one manageable workflow.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg> },
                { title: "AI-Enhanced Support", description: "Automate initial engagement and prioritize live agent intervention intelligently, improving resolution speed.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" /></svg> },
                { title: "Comprehensive Oversight", description: "Maintain full visibility with persistent chat logs and superadmin auditing capabilities.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> },
                { title: "Security & Compliance", description: "Built with industry-standard authentication and data management practices.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 11c0 1.1-.9 2-2 2s-2-.9-2-2 2-4 2-4 2 2.9 2 4zm0 0c0 1.1.9 2 2 2s2-.9 2-2-2-4-2-4-2 2.9-2 4zm-6 7v-2c0-2.2 3.6-4 6-4s6 1.8 6 4v2H6z" /></svg> },
                { title: "Extensibility & Customization", description: "Designed to integrate additional channels and features, supporting your evolving business needs.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" /></svg> },
                { title: "Seamless Platform Integration", description: "Connect effortlessly with your existing tools and CRMs to streamline workflows without disrupting your tech stack.", icon: <svg className="w-10 h-10 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6h4M6 10v4m12-4v4M7.5 7.5l9 9M16.5 7.5l-9 9" /></svg> }
              ];
              return (
                <>
                  <ul className="md:hidden flex overflow-x-auto space-x-4 snap-x snap-mandatory pb-4 -mx-2 px-2">
                    {features.map((item, index) => (
                      <motion.li key={index} variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.5 }} className="snap-start flex-shrink-0 w-80 bg-white p-6 rounded-xl shadow-lg border border-gray-100">
                        <div className="flex items-center mb-4">{item.icon}</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </motion.li>
                    ))}
                  </ul>
                  <motion.ul variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.1 }} className="hidden md:grid md:grid-cols-3 gap-8">
                    {features.map((item, index) => (
                      <motion.li key={index} variants={fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                        <div className="flex items-center mb-4">{item.icon}</div>
                        <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                        <p className="text-gray-600">{item.description}</p>
                      </motion.li>
                    ))}
                  </motion.ul>
                </>
              );
            })()}
          </div>
          <motion.p variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true }} className="mt-12 text-center text-lg font-semibold text-gray-900 max-w-3xl mx-auto">
            Void is not just software — it is your partner in transforming customer support into a competitive advantage.
          </motion.p>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <motion.h2 variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.8 }} className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-12">
            How It Works
          </motion.h2>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              variants={slideIn('right')} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.3 }}
              className="order-2 lg:order-1"
            >
              <img src='/images/Howitworks.png' alt="How it works diagram" />
            </motion.div>
            <motion.div 
              variants={staggerContainer} initial="initial" whileInView="animate" viewport={{ once: true, amount: 0.2 }}
              className="order-1 lg:order-2 space-y-8"
            >
              {[
                { step: 1, title: "Customer Initiates Contact", description: "Via your website chat or WhatsApp, customers begin a conversation with Void’s AI-powered assistant." },
                { step: 2, title: "AI Handles Initial Interaction", description: "The AI bot provides immediate answers to common questions, triages requests, and identifies when live agent intervention is needed." },
                { step: 3, title: "Seamless Transfer to Agents", description: "Live support requests are queued and assigned to available agents through the real-time dashboard, ensuring personalized follow-up." },
                { step: 4, title: "Full Conversation Logging & Supervision", description: "All messages and system events are logged securely, enabling quality audits, analytics, and continuous improvement." }
              ].map(({ step, title, description }) => (
                <motion.div key={step} variants={fadeInUp} className="flex items-start gap-4">
                  <div className="flex-shrink-0 w-12 h-12 bg-[#16a085] text-white rounded-full flex items-center justify-center text-xl font-bold">
                    {step}
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900">{title}</h3>
                    <p className="mt-2 text-gray-600">{description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Customer Success Stories */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 text-center text-gray-600">
        <motion.blockquote 
          variants={fadeInUp} initial="initial" whileInView="animate" viewport={{ once: true }}
          className="text-xl font-semibold leading-relaxed max-w-3xl mx-auto"
        >
          “Our Smart Assist is fully powered by Void’s logic—seamlessly integrated across our website, WhatsApp, and Facebook. It’s transformed how we support users.” <br />
          <cite className="mt-4 not-italic block">Dark Technologies</cite>
        </motion.blockquote>
      </section>

    </main>
  );
}